import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminProductsAPI } from '../services/api';
import { useParams, useNavigate, Link } from 'react-router-dom';
import CustomSelect from './CustomSelect';
import {
  Package,
  Tag,
  IndianRupee,
  Boxes,
  FileEdit,
  X,
  CheckCircle2,
  Info,
  Image,
  Eye,
  Check,
  Loader2,
  AlignLeft
} from 'lucide-react';
// Uses global header via Layout

const EditProduct = () => {
  const { isAdmin } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [imagePreview, setImagePreview] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [imageFile, setImageFile] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    stock_quantity: ''
  });

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminProductsAPI.getById(id);
      const productData = response.data.product;
      setProduct(productData);
      setFormData({
        name: productData.name,
        description: productData.description,
        price: productData.price.toString(),
        category: productData.category,
        stock_quantity: productData.stock_quantity.toString(),
      });
      setImagePreview(productData.image_url);
      
      // Fetch categories for dropdown
      try {
        const productsResponse = await adminProductsAPI.getAll();
        const uniqueCategories = [...new Set(productsResponse.data.products.map(p => p.category))];
        setCategories(uniqueCategories);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    } catch (err) {
      setError('Failed to fetch product');
      console.error('Error fetching product:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isAdmin()) {
      setError('Access denied. Admin privileges required.');
      setLoading(false);
      return;
    }
    fetchProduct();
  }, [isAdmin, fetchProduct]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Update image preview
    if (name === 'image_url') {
      setImagePreview(value);
      setImageFile(null); // Clear file if manually entering url
    }
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      if (formErrors.imageFile) {
        setFormErrors((prev) => ({ ...prev, imageFile: '' }));
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Product name is required';
    } else if (formData.name.length < 2) {
      errors.name = 'Product name must be at least 2 characters';
    } else if (formData.name.length > 100) {
      errors.name = 'Product name must be less than 100 characters';
    }
    
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    } else if (formData.description.length < 10) {
      errors.description = 'Description must be at least 10 characters';
    } else if (formData.description.length > 1000) {
      errors.description = 'Description must be less than 1000 characters';
    }
    
    if (!formData.price || parseFloat(formData.price) <= 0) {
      errors.price = 'Price must be greater than 0';
    }
    
    if (!formData.category.trim()) {
      errors.category = 'Category is required';
    } else if (formData.category.length < 2) {
      errors.category = 'Category must be at least 2 characters';
    } else if (formData.category.length > 50) {
      errors.category = 'Category must be less than 50 characters';
    }
    
    if (formData.stock_quantity === '' || parseInt(formData.stock_quantity) < 0) {
      errors.stock_quantity = 'Stock quantity must be 0 or greater';
    }
    
    if (!imageFile && !imagePreview) {
      errors.imageFile = 'Image file is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setSaving(true);
      setSuccessMessage('');
      await adminProductsAPI.update(id, { ...formData, image: imageFile });
      setSuccessMessage('Product updated successfully!');
      setTimeout(() => {
        navigate('/admin/products');
      }, 1500);
    } catch (err) {
      console.error('Error updating product:', err);
      const apiErr = err.response?.data;
      setFormErrors({ submit: apiErr?.errors ? apiErr.errors.join(', ') : (apiErr?.error || 'Failed to update product') });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/products');
  };

  if (!isAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
          <Link to="/dashboard" className="text-blue-600 hover:text-blue-800 mt-4 inline-block">
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link to="/admin/products" className="text-blue-600 hover:text-blue-800">
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Main Content */}
      <div className="max-w-5xl mx-auto py-4 sm:py-8 sm:px-6 lg:px-8">
        <div className="px-2 sm:px-4 py-4 sm:py-6 sm:px-0">
            {/* Modern Product Preview Card */}
          {product && (
            <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl border border-gray-200 p-3 sm:p-4 md:p-6 mb-8 transform transition-all duration-300 hover:shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Package className="w-5 h-5 mr-2 text-green-600" />
                  Current Product
                </h3>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  product.available 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {product.available ? 'In Stock' : 'Out of Stock'}
                </span>
              </div>
              <div className="flex items-center space-x-6">
                <div className="relative">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-28 h-28 object-cover rounded-xl shadow-lg ring-2 ring-gray-200"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/112x112?text=No+Image';
                    }}
                  />
                </div>
                <div className="flex-1">
                  <h4 className="text-2xl font-bold text-gray-900 mb-1">{product.name}</h4>
                  <div className="flex items-center space-x-4 mb-2">
                    <span className="inline-flex items-center px-3 py-1 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium">
                      <Tag className="w-4 h-4 mr-1.5" />
                      {product.category}
                    </span>
                  </div>
                  <div className="flex items-center space-x-6 mt-3">
                    <div className="flex items-center">
                      <span className="text-2xl font-bold text-green-600 mr-1">{product.formatted_price}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Boxes className="w-4 h-4 mr-1.5" />
                      <span className="text-sm font-medium">Stock: {product.stock_quantity}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Modern Success Message */}
          {successMessage && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-lg p-4 mb-6 flex items-center shadow-md animate-slide-in">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-semibold text-green-900">{successMessage}</p>
                <p className="text-xs text-green-700 mt-0.5">Redirecting to products page...</p>
              </div>
            </div>
          )}

          {/* Modern Edit Form */}
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Form Header with Gradient */}
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-3 sm:px-4 md:px-8 py-4 md:py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <FileEdit className="w-6 h-6 text-white" />
                  </div>
                </div>
                <Link
                  to="/admin/products"
                  className="md:hidden text-white/80 hover:text-white transition-colors"
                  aria-label="Back to products"
                >
                  <X className="w-6 h-6" />
                </Link>
              </div>
            </div>

            <div className="p-3 sm:p-4 md:p-8">
              {formErrors.submit && (
                <div className="bg-gradient-to-r from-red-50 to-rose-50 border-l-4 border-red-500 rounded-lg p-4 mb-6 flex items-center shadow-sm">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                      <X className="h-5 w-5 text-white" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-semibold text-red-900">{formErrors.submit}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleUpdateProduct}>
                <div className="space-y-8">
                  {/* Basic Information Section */}
                  <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-3 sm:p-4 md:p-6 border border-gray-100">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Info className="w-5 h-5 text-green-600" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900">Basic Information</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <Tag className="w-4 h-4 mr-1.5 text-gray-400" />
                        Product Name <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        maxLength={100}
                        placeholder="Enter product name"
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 shadow-sm hover:shadow-md ${
                          formErrors.name 
                            ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500' 
                            : 'border-gray-300 bg-white hover:border-gray-400'
                        }`}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {formErrors.name && (
                          <p className="text-xs text-red-600">{formErrors.name}</p>
                        )}
                        <p className="text-xs text-gray-500 ml-auto">
                          {formData.name.length}/100 characters
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <Tag className="w-4 h-4 mr-1.5 text-gray-400" />
                        Category <span className="text-red-500 ml-1">*</span>
                      </label>
                      {categories.length > 0 ? (
                        <CustomSelect
                          id="category-select-edit"
                          value={formData.category}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '__new__') {
                              const newCategory = prompt('Enter new category name:');
                              if (newCategory) {
                                setFormData(prev => ({ ...prev, category: newCategory }));
                              }
                            } else {
                              setFormData(prev => ({ ...prev, category: value }));
                            }
                          }}
                          options={[
                            { value: '', label: 'Select or enter category' },
                            ...categories.map(cat => ({ value: cat, label: cat })),
                            { value: '__new__', label: '+ Add New Category' }
                          ]}
                          className={formErrors.category ? 'border-red-300' : ''}
                        />
                      ) : (
                        <input
                          type="text"
                          name="category"
                          value={formData.category}
                          onChange={handleInputChange}
                          required
                          maxLength={50}
                          placeholder="Enter category"
                          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 shadow-sm hover:shadow-md ${
                            formErrors.category 
                              ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500' 
                              : 'border-gray-300 bg-white hover:border-gray-400'
                          }`}
                        />
                      )}
                      {formErrors.category && (
                        <p className="text-xs text-red-600 mt-1">{formErrors.category}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pricing & Inventory Section */}
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <IndianRupee className="w-4 h-4 mr-1.5 text-gray-400" />
                        Price (₹) <span className="text-red-500 ml-1">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold text-lg">₹</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          name="price"
                          value={formData.price}
                          onChange={handleInputChange}
                          required
                          placeholder="0.00"
                          className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 shadow-sm hover:shadow-md ${
                            formErrors.price 
                              ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500' 
                              : 'border-gray-300 bg-white hover:border-gray-400'
                          }`}
                        />
                      </div>
                      {formErrors.price && (
                        <p className="text-xs text-red-600 mt-1">{formErrors.price}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                        <Boxes className="w-4 h-4 mr-1.5 text-gray-400" />
                        Stock Quantity <span className="text-red-500 ml-1">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        name="stock_quantity"
                        value={formData.stock_quantity}
                        onChange={handleInputChange}
                        required
                        placeholder="0"
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 shadow-sm hover:shadow-md ${
                          formErrors.stock_quantity 
                            ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500' 
                            : 'border-gray-300 bg-white hover:border-gray-400'
                        }`}
                      />
                      {formErrors.stock_quantity && (
                        <p className="text-xs text-red-600 mt-1">{formErrors.stock_quantity}</p>
                      )}
                      {formData.stock_quantity && parseInt(formData.stock_quantity) <= 5 && (
                        <p className="text-xs text-yellow-600 mt-1">
                          ⚠️ Low stock warning will be shown
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description Section */}
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-100">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <AlignLeft className="w-4 h-4 mr-1.5 text-gray-400" />
                      Product Description <span className="text-red-500 ml-1">*</span>
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      required
                      rows={4}
                      maxLength={1000}
                      placeholder="Enter detailed product description..."
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 shadow-sm hover:shadow-md resize-none ${
                        formErrors.description 
                          ? 'border-red-300 bg-red-50 focus:ring-red-500 focus:border-red-500' 
                          : 'border-gray-300 bg-white hover:border-gray-400'
                      }`}
                    />
                    <div className="flex justify-between items-center mt-1">
                      {formErrors.description && (
                        <p className="text-xs text-red-600">{formErrors.description}</p>
                      )}
                      <p className="text-xs text-gray-500 ml-auto">
                        {formData.description.length}/1000 characters
                      </p>
                    </div>
                  </div>
                </div>

                {/* Image Section */}
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-6 border border-gray-100">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <Image className="w-4 h-4 mr-1.5 text-gray-400" />
                      Image <span className="text-red-500 ml-1">*</span>
                    </label>
                    <div className="flex flex-col space-y-4">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageFileChange}
                        className="w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 shadow-sm hover:shadow-md"
                      />
                      {formErrors.imageFile && (
                        <p className="text-xs text-red-600 mt-1">{formErrors.imageFile}</p>
                      )}
                      {imagePreview && (
                        <div className="mt-6">
                          <p className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                            <Eye className="w-4 h-4 mr-1.5 text-gray-400" />
                            Image Preview
                          </p>
                          <div className="relative inline-block group">
                            <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-emerald-400/20 rounded-xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <img
                              src={imagePreview}
                              alt="Preview"
                              className="relative w-40 h-40 object-cover rounded-xl border-2 border-gray-200 shadow-lg transform transition-transform duration-300 group-hover:scale-105"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/160x160?text=Invalid+Image';
                                e.target.className = 'relative w-40 h-40 object-cover rounded-xl border-2 border-red-200 shadow-lg';
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4 mt-8 pt-8 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={saving}
                  className="px-8 py-3.5 border-2 border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-sm hover:shadow-md flex items-center justify-center"
                >
                  <X className="w-5 h-5 mr-2" />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-8 py-3.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
                >
                  {saving ? (
                    <>
                      <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      Update Product
                    </>
                  )}
                </button>

                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProduct;
